// Post-purchase welcome panel.
//
// Fires the first time a paid user lands anywhere in the app AFTER their
// tier becomes non-empty. Dismissal is tracked per-tier in localStorage so
// upgrading from Lite → Pro re-shows the celebration with new bullets.
//
// Lifecycle:
//   1. User completes Stripe checkout → returns to /dashboard or any route.
//   2. AuthContext refreshes user from /auth/me → user.tier.tier becomes
//      "investor_lite" | "investor_pro" | "investor_plus".
//   3. On mount, this component checks: tier set AND
//      localStorage["bl_welcome_seen_<tier>"] is absent → render full
//      celebratory overlay with 3 quick wins, mark dismissed on close.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { PartyPopper, Upload, Share2, BookOpen, ArrowRight, X } from "lucide-react";

const TIER_LABEL = {
    investor_lite: "Buyer's Leverage Lite",
    investor_pro: "Buyer's Leverage Pro",
    investor_plus: "Buyer's Leverage Plus",
};

const QUICK_WINS = [
    {
        icon: Upload,
        title: "Upload your first inspection",
        body: "Drop the PDF and get prioritised repairs + cost estimates within minutes.",
        cta: "Upload now",
        to: "/dashboard?source=welcome",
    },
    {
        icon: Share2,
        title: "Share with your agent",
        body: "Generate a clean read-only link so your agent sees the negotiation summary.",
        cta: "How it works",
        to: "/learn/sharing-your-report-with-your-agent?source=welcome",
    },
    {
        icon: BookOpen,
        title: "Browse the learn library",
        body: "38 evergreen guides on what to negotiate, repair-cost benchmarks, and more.",
        cta: "Open library",
        to: "/learn?source=welcome",
    },
];

export default function WelcomePanel() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [tier, setTier] = useState("");

    useEffect(() => {
        const t = user?.tier?.tier;
        if (!t || t === "none") return;
        try {
            const seenKey = `bl_welcome_seen_${t}`;
            if (localStorage.getItem(seenKey)) return;
            setTier(t);
            setOpen(true);
        } catch {
            /* localStorage disabled — silently skip */
        }
    }, [user]);

    if (!open) return null;

    const dismiss = () => {
        try {
            localStorage.setItem(`bl_welcome_seen_${tier}`, new Date().toISOString());
        } catch { /* ignore */ }
        setOpen(false);
    };

    const goTo = (to) => {
        dismiss();
        navigate(to);
    };

    const tierName = TIER_LABEL[tier] || "Buyer's Leverage";

    return (
        <div
            className="fixed inset-0 z-[85] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
            data-testid="welcome-panel"
        >
            <div className="bl-card max-w-lg w-full overflow-hidden relative shadow-2xl">
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Close"
                    className="absolute top-3 right-3 p-1.5 rounded-full text-bl-muted hover:text-bl"
                    data-testid="welcome-panel-close"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="bg-bl-teal-tint px-6 pt-7 pb-5">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/80 text-[10px] tracking-[0.2em] uppercase text-bl-primary font-semibold">
                        <PartyPopper className="w-3 h-3" />
                        Welcome aboard
                    </div>
                    <h2 className="mt-3 font-display font-semibold text-2xl text-bl leading-tight">
                        You're on {tierName}.
                    </h2>
                    <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                        Here are 3 quick wins to make the most of your subscription
                        in the next 10 minutes.
                    </p>
                </div>

                <ul className="px-6 py-5 space-y-3" data-testid="welcome-panel-wins">
                    {QUICK_WINS.map((w) => (
                        <li
                            key={w.title}
                            className="flex items-start gap-3 p-3 rounded border border-bl hover:border-bl-primary transition-colors cursor-pointer group"
                            onClick={() => goTo(w.to)}
                            data-testid={`welcome-panel-win-${w.title.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                            <w.icon
                                className="w-4 h-4 mt-0.5 text-bl-primary shrink-0"
                                aria-hidden
                            />
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-semibold text-sm text-bl">
                                    {w.title}
                                </div>
                                <div className="mt-0.5 text-[11px] text-bl-muted leading-snug">
                                    {w.body}
                                </div>
                            </div>
                            <ArrowRight
                                className="w-4 h-4 text-bl-muted group-hover:text-bl-primary transition-colors mt-1"
                                aria-hidden
                            />
                        </li>
                    ))}
                </ul>

                <div className="px-6 pb-5 flex justify-end">
                    <button
                        type="button"
                        onClick={dismiss}
                        className="bl-btn-ghost text-sm"
                        data-testid="welcome-panel-dismiss"
                    >
                        I'll explore on my own
                    </button>
                </div>
            </div>
        </div>
    );
}
